"use client";

import React from "react";
import { Loader } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function PrivateConsultation() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="private-consultation" className="bg-white py-32 px-4 md:px-8 flex justify-center items-center min-h-[80vh]">
      <div ref={ref} className={`w-full max-w-2xl transition-all duration-700 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
        <Card className="border border-border/40 shadow-2xl bg-[#192332] backdrop-blur-md">
          <CardHeader className="text-center space-y-4 pb-8">
            <CardTitle className="text-4xl font-serif font-medium tracking-tight !text-[hsl(0,0%,100%)] md:text-5xl">Request Your Private Consultation</CardTitle>
            
          </CardHeader>
          <CardContent>
            <FormProvider formId="private_consultation_form_contact" sectionName="private-consultation" defaultValues={{
            full_name: "",
            email: "",
            phone: "",
            subject: "",
            message: ""
          }} validationRules={{
            full_name: {
              required: "Full Name is required"
            },
            email: {
              required: "Email Address is required",
              pattern: {
                value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                message: "Please enter a valid email address"
              }
            },
            subject: {
              required: "Subject is required"
            },
            message: {
              required: "Message is required"
            }
          }} submitText="Submit Request" successMessage="Your request has been received. We will contact you shortly to schedule your session." errorMessage="There was a problem submitting your request. Please try again.">
              <ConsultationForm />
            </FormProvider>
          </CardContent>
        </Card>
      </div>
    </section>;
}
function ConsultationForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="p-10 bg-accent/10 border border-accent/20 rounded-sm text-center animate-in fade-in zoom-in duration-500">
        <h3 className="text-2xl font-serif text-primary mb-3">Request Received</h3>
        <p className="text-muted-foreground leading-relaxed">{successMessage}</p>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-8">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FormField control={control} name="full_name" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-white">Full Name</FormLabel>
              <FormControl>
                <Input placeholder="Jane Doe" className="bg-white/5 border-white/20 text-white placeholder:text-white/40 focus:border-[#c7b08a] focus:ring-[#c7b08a]/20" {...field} />
              </FormControl>
              <FormDescription className="text-white/60">Enter your first and last name.</FormDescription>
              <FormMessage className="text-red-400" />
            </FormItem>} />

        <FormField control={control} name="email" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-white">Email Address</FormLabel>
              <FormControl>
                <Input type="email" placeholder="jane.doe@example.com" className="bg-white/5 border-white/20 text-white placeholder:text-white/40 focus:border-[#c7b08a] focus:ring-[#c7b08a]/20" {...field} />
              </FormControl>
              <FormDescription className="text-white/60">
                We'll use this to send your consultation details.
              </FormDescription>
              <FormMessage className="text-red-400" />
            </FormItem>} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FormField control={control} name="phone" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-white">Phone Number</FormLabel>
              <FormControl>
                <Input type="tel" placeholder="+1 555 123 4567" className="bg-white/5 border-white/20 text-white placeholder:text-white/40 focus:border-[#c7b08a] focus:ring-[#c7b08a]/20" {...field} />
              </FormControl>
              <FormDescription className="text-white/60">Optional – for quicker follow‑up.</FormDescription>
              <FormMessage className="text-red-400" />
            </FormItem>} />

        <FormField control={control} name="subject" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-white">Subject</FormLabel>
              <FormControl>
                <Input placeholder="Consultation Inquiry" className="bg-white/5 border-white/20 text-white placeholder:text-white/40 focus:border-[#c7b08a] focus:ring-[#c7b08a]/20" {...field} />
              </FormControl>
              <FormDescription className="text-white/60">
                Briefly state the reason for your request.
              </FormDescription>
              <FormMessage className="text-red-400" />
            </FormItem>} />
      </div>

      <FormField control={control} name="message" render={({
      field
    }) => <FormItem>
            <FormLabel className="text-white">Message</FormLabel>
            <FormControl>
              <Textarea placeholder="Provide a short overview of your situation and what you hope to achieve..." className="min-h-[120px] bg-white/5 border-white/20 text-white placeholder:text-white/40 focus:border-[#c7b08a] focus:ring-[#c7b08a]/20" {...field} />
            </FormControl>
            <FormDescription className="text-white/60">
              The more detail you give, the better we can prepare for your private
              consultation.
            </FormDescription>
            <FormMessage className="text-red-400" />
          </FormItem>} />

      {submitError && <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-sm">
          <p className="text-destructive text-sm font-medium">{submitError}</p>
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full bg-[#c7b08a] text-[#192332] hover:bg-[#c7b08a]/90 h-14 text-base font-semibold tracking-widest uppercase transition-all duration-300">
        {isSubmitting ? <>
            <Loader className="animate-spin mr-2 size-5" />
            Submitting Request...
          </> : "Submit Request"}
      </Button>
    </Form>;
}